import React, { useState, useMemo, useEffect } from 'react';
import { LEISTUNGEN, type Leistung, type LeistungsTyp } from '../data/leistungskatalog';
import { euroToEuroCents, type EuroCents } from '../../../types/branded';
import { useMandantStore } from '../../../store/mandantStore';
import { loadSetting, saveSetting } from '../../../services/settingsService';

// ── Typen ──────────────────────────────────────────────────────────────────

interface HonorarPosition {
  leistungCode: string;
  preisNetto:   EuroCents;   // überschriebener Preis (oder Standard)
  aktiv:        boolean;
}

type HonorarMap = Map<string, HonorarPosition>;

const TYP_COLORS: Record<LeistungsTyp, string> = {
  VGÜ:      'bg-blue-100   text-blue-800',
  Beratung: 'bg-purple-100 text-purple-800',
  Begehung: 'bg-green-100  text-green-800',
  Gutachten:'bg-orange-100 text-orange-800',
  Schulung: 'bg-teal-100   text-teal-800',
  Sonstige: 'bg-gray-100   text-gray-700',
};

function formatEuro(cents: EuroCents): string {
  return (cents / 100).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ── Preiseingabe-Feld ──────────────────────────────────────────────────────

function PreisInput({
  value,
  onChange,
  disabled,
}: {
  value: EuroCents;
  onChange: (v: EuroCents) => void;
  disabled: boolean;
}): React.JSX.Element {
  const [raw, setRaw] = useState<string>((value / 100).toFixed(2));

  const commit = (): void => {
    const parsed = parseFloat(raw.replace(',', '.'));
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(euroToEuroCents(parsed));
      setRaw((euroToEuroCents(parsed) / 100).toFixed(2));
    } else {
      setRaw((value / 100).toFixed(2));
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={raw}
        onChange={e => { setRaw(e.target.value); }}
        onBlur={commit}
        onKeyDown={e => { if (e.key === 'Enter') { commit(); } }}
        disabled={disabled}
        className={`w-28 text-right px-3 py-1.5 text-sm rounded-lg border
          ${disabled
            ? 'bg-gray-50 dark:bg-gray-800 text-gray-400 border-gray-100 dark:border-gray-700 cursor-not-allowed'
            : 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500'
          }`}
      />
      <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">€</span>
    </div>
  );
}

// ── Zeile ──────────────────────────────────────────────────────────────────

function LeistungsZeile({
  leistung,
  pos,
  onChange,
}: {
  leistung: Leistung;
  pos:      HonorarPosition;
  onChange: (updated: HonorarPosition) => void;
}): React.JSX.Element {
  const isModified = pos.preisNetto !== leistung.preisNetto;

  return (
    <tr className={`border-b border-gray-100 dark:border-gray-800 transition-colors
        ${!pos.aktiv ? 'opacity-40' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}>
      {/* Aktiv-Toggle */}
      <td className="px-4 py-3 w-10">
        <input
          type="checkbox"
          checked={pos.aktiv}
          onChange={e => { onChange({ ...pos, aktiv: e.target.checked }); }}
          className="rounded border-gray-300 text-blue-600"
        />
      </td>

      {/* Code */}
      <td className="px-4 py-3">
        <span className="font-mono text-xs font-bold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded">
          {leistung.code}
        </span>
      </td>

      {/* Bezeichnung */}
      <td className="px-4 py-3">
        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{leistung.bezeichnung}</p>
        {leistung.beschreibung !== undefined && (
          <p className="text-xs text-gray-400 mt-0.5 hidden lg:block">{leistung.beschreibung}</p>
        )}
      </td>

      {/* Typ */}
      <td className="px-4 py-3 hidden sm:table-cell">
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYP_COLORS[leistung.typ]}`}>
          {leistung.typ}
        </span>
      </td>

      {/* Einheit */}
      <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
        {leistung.einheit}
      </td>

      {/* USt */}
      <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
        {leistung.ustSatz === 0 ? 'steuerfrei' : `${leistung.ustSatz} %`}
      </td>

      {/* Standardpreis */}
      <td className="px-4 py-3 text-sm text-gray-400 text-right hidden lg:table-cell">
        {formatEuro(leistung.preisNetto)}
      </td>

      {/* Vereinbarter Preis */}
      <td className="px-4 py-3 text-right">
        <div className="flex items-center justify-end gap-2">
          {isModified && (
            <button
              type="button"
              onClick={() => { onChange({ ...pos, preisNetto: leistung.preisNetto }); }}
              className="text-xs text-orange-500 hover:text-orange-700 flex-shrink-0"
              title="Auf Standardpreis zurücksetzen"
            >
              ↺
            </button>
          )}
          <PreisInput
            value={pos.preisNetto}
            onChange={v => { onChange({ ...pos, preisNetto: v }); }}
            disabled={!pos.aktiv}
          />
        </div>
      </td>
    </tr>
  );
}

// ── Hauptkomponente ────────────────────────────────────────────────────────

function defaultMap(): HonorarMap {
  const m: HonorarMap = new Map();
  for (const l of LEISTUNGEN) {
    m.set(l.code, { leistungCode: l.code, preisNetto: l.preisNetto, aktiv: true });
  }
  return m;
}

export function Honorarvereinbarung(): React.JSX.Element {
  const mandant = useMandantStore(s => s.getAktiverMandant());
  const [saved,      setSaved]      = useState(false);
  const [honorarMap, setHonorarMap] = useState<HonorarMap>(defaultMap);

  const storageKey = `honorar_${mandant?.id ?? 'global'}`;

  useEffect(() => {
    void loadSetting<HonorarPosition[]>(storageKey, []).then(saved => {
      if (saved.length === 0) { setHonorarMap(defaultMap()); return; }
      const m = defaultMap();
      for (const p of saved) { m.set(p.leistungCode, p); }
      setHonorarMap(m);
    });
  }, [mandant?.id]);

  const [filterTyp, setFilterTyp] = useState<LeistungsTyp | 'Alle'>('Alle');
  const [showModifiedOnly, setShowModifiedOnly] = useState(false);

  const filtered = useMemo(() => LEISTUNGEN.filter(l => {
    const matchTyp  = filterTyp === 'Alle' || l.typ === filterTyp;
    const pos       = honorarMap.get(l.code);
    const isModified = pos !== undefined && pos.preisNetto !== l.preisNetto;
    const matchMod  = !showModifiedOnly || isModified;
    return matchTyp && matchMod;
  }), [filterTyp, showModifiedOnly, honorarMap]);

  const modifiedCount = useMemo(() =>
    LEISTUNGEN.filter(l => {
      const pos = honorarMap.get(l.code);
      return pos !== undefined && pos.preisNetto !== l.preisNetto;
    }).length
  , [honorarMap]);

  const handleChange = (code: string, updated: HonorarPosition): void => {
    setSaved(false);
    setHonorarMap(prev => {
      const next = new Map(prev);
      next.set(code, updated);
      return next;
    });
  };

  const handleSave = (): void => {
    const positions = Array.from(honorarMap.values());
    void saveSetting(storageKey, positions).then(() => {
      setSaved(true);
      setTimeout(() => { setSaved(false); }, 2500);
    });
  };

  const TYPEN: LeistungsTyp[] = ['VGÜ', 'Beratung', 'Begehung', 'Gutachten', 'Schulung', 'Sonstige'];

  return (
    <div className="p-6 space-y-5 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Honorarvereinbarung</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Individuelle Preise für
            {mandant !== null && mandant !== undefined
              ? <span className="ml-1 font-medium" style={{ color: mandant.farbe }}>{mandant.name}</span>
              : ' diesen Mandanten'
            }
          </p>
        </div>
        <div className="flex items-center gap-3">
          {modifiedCount > 0 && (
            <span className="text-xs bg-orange-100 text-orange-700 dark:bg-orange-950/40 dark:text-orange-400 px-2.5 py-1 rounded-full font-medium">
              {modifiedCount} geändert
            </span>
          )}
          <button
            onClick={handleSave}
            className={`btn-primary transition-all ${saved ? '!bg-green-600' : ''}`}
          >
            {saved ? '✓ Gespeichert' : 'Speichern'}
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex flex-wrap gap-2">
          {(['Alle', ...TYPEN] as const).map(t => (
            <button
              key={t}
              onClick={() => { setFilterTyp(t); }}
              className={`text-xs px-3 py-1 rounded-full font-medium transition-colors
                ${filterTyp === t
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
            >
              {t}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 ml-auto text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
          <input
            type="checkbox"
            checked={showModifiedOnly}
            onChange={e => { setShowModifiedOnly(e.target.checked); }}
            className="rounded border-gray-300 text-blue-600"
          />
          Nur geänderte
        </label>
      </div>

      {/* Tabelle */}
      <div className="card overflow-hidden p-0">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-4 py-3 w-10" />
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 w-24">Code</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Leistung</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden sm:table-cell">Typ</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden md:table-cell">Einheit</th>
              <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden md:table-cell">USt</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3 hidden lg:table-cell">Standard (€)</th>
              <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 py-3">Vereinbart (€)</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-gray-400 py-10 italic">
                  Keine Leistungen gefunden.
                </td>
              </tr>
            )}
            {filtered.map(l => {
              const pos = honorarMap.get(l.code);
              if (pos === undefined) { return null; }
              return (
                <LeistungsZeile
                  key={l.code}
                  leistung={l}
                  pos={pos}
                  onChange={updated => { handleChange(l.code, updated); }}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-gray-400 text-center">
        ✅ Persistenz in Dexie v5 · pro Mandant gespeichert
      </p>
    </div>
  );
}
